import { Box } from '@lifeomic/chroma-react/components/Box';
import React, { useCallback, useMemo, useState } from 'react';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import {
  ACTION_PLAN_SYSTEMS,
  ActionPlanActivity,
  ACTION_PLAN_SYSTEMS_ICONS,
} from './action-plan';
import { ActionActivity } from './ActionActivity';
import { ActionItemModal } from './ActionItemModal';
import { defineMessages, MessageDescriptor, useIntl } from 'react-intl';
import { CheckList } from '@lifeomic/chromicons';
import { lighten, alpha } from '@mui/material/styles';
import {
  useExecutiveSummaryData,
  useExecutiveSummarySelection,
  useExecutiveSummaryUI,
} from '../ExecutiveSummary/Context';
import { ViewMode } from '../AITools/Attachments/InlineAttachment';
import EmptyPageState from '../../../EmptyPageState';
import { DeleteModal } from './DeleteActionItemModal';

const useStyles = makeStyles((theme: Theme) => ({
  actionsList: {
    boxShadow: theme.boxShadows.table,
    flex: 1,
    maxWidth: '100%',
    maxHeight: '100%',
    minHeight: 0,
    overflowY: 'auto',
  },
  systemHeader: {
    alignItems: 'center',
    backgroundColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.06)
        : lighten(theme.palette.background.default, 0.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderTop: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.hint,
    display: 'flex',
    fontSize: theme.pxToRem(12),
    fontWeight: theme.typography.fontWeightBold,
    gap: theme.spacing(1),
    letterSpacing: theme.pxToRem(1.2),
    padding: theme.spacing(1, 2),
    position: 'sticky',
    textTransform: 'uppercase',
    top: 0,
    zIndex: 1,
  },

  emptyStateContainer: {
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(5),
    backgroundColor: theme.palette.background.paper,
  },
}));

const messages = defineMessages({
  addActionItem: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.actionItem',
    defaultMessage: 'New Action Item',
  },
  editActionItem: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.actionItem',
    defaultMessage: 'Edit Action Item',
  },
  noActionItems: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.noActionItems',
    defaultMessage:
      'When ready, your action plan will be shown on the home screen of your app.',
  },
});

const systemHeaders = defineMessages<
  MessageDescriptor,
  Record<(typeof ACTION_PLAN_SYSTEMS)[number], MessageDescriptor>
>({
  general: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.general',
    defaultMessage: 'General',
  },
  heart: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.heart',
    defaultMessage: 'Heart Health',
  },
  brain: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.brain',
    defaultMessage: 'Brain Health',
  },
  cancer: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.cancer',
    defaultMessage: 'Cancer Risk',
  },
  metabolic: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.metabolic',
    defaultMessage: 'Metabolic Health',
  },
  musculoskeletal: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.musculoskeletal',
    defaultMessage: 'Musculoskeletal Health',
  },
  supplements: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.supplements',
    defaultMessage: 'Supplements & Medications',
  },
  cardiometabolic: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.cardiometabolic',
    defaultMessage: 'Cardiometabolic',
  },
  immune: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.immune',
    defaultMessage: 'Immune Health',
  },
  hormones: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.hormones',
    defaultMessage: 'Hormonal Health',
  },
  energy: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.energy',
    defaultMessage: 'Energy Health',
  },
  detoxification: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.detoxification',
    defaultMessage: 'Detoxification',
  },
  gastrointestinal: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.gastrointestinal',
    defaultMessage: 'Gastrointestinal Health',
  },
});

interface ActionItemsProps {
  removeActivity: (activity: fhir.CarePlanActivity) => void;
  addActionItem: (activity: fhir.CarePlanActivity) => void;
  updateActionItem: (
    previousActivity: fhir.CarePlanActivity,
    activity: fhir.CarePlanActivity,
  ) => void;
  toggleActionItemStatus: (activity: fhir.CarePlanActivity) => void;
  forcedViewMode?: ViewMode;
  carePlan?: fhir.CarePlan;
}

export const ActionItems = ({
  removeActivity,
  addActionItem,
  updateActionItem,
  toggleActionItemStatus,
  forcedViewMode,
  carePlan,
}: ActionItemsProps) => {
  const classes = useStyles({});

  const { formatMessage } = useIntl();

  const { executiveSummary } = useExecutiveSummaryData();
  const {
    activeActionItem,
    setActiveActionItem,
    selectedActionItem,
    setSelectedActionItem,
    viewMode: _viewMode,
  } = useExecutiveSummarySelection();
  const { isActionItemModalOpen, setIsActionItemModalOpen } =
    useExecutiveSummaryUI();

  const viewMode = forcedViewMode ?? _viewMode;
  const displayCarePlan = carePlan ?? executiveSummary?.carePlan;

  const onActivityModalClose = () => {
    setIsActionItemModalOpen(false);
    setSelectedActionItem(undefined);
    setActiveActionItem(new ActionPlanActivity());
  };

  const onConfirm = (activity: fhir.CarePlanActivity) => {
    if (selectedActionItem !== undefined) {
      updateActionItem(selectedActionItem, activity);
    } else {
      addActionItem(activity);
    }
    onActivityModalClose();
  };

  const editActionPlanActivity = useCallback(
    (activity: fhir.CarePlanActivity) => {
      setActiveActionItem(
        new ActionPlanActivity(executiveSummary?.getActionItem(activity)),
      );
      setSelectedActionItem(activity);
      setIsActionItemModalOpen(true);
    },
    [
      setActiveActionItem,
      executiveSummary,
      setSelectedActionItem,
      setIsActionItemModalOpen,
    ],
  );

  const toggleVisibility = useCallback(
    (activity: fhir.CarePlanActivity) => {
      toggleActionItemStatus(activity);
    },
    [toggleActionItemStatus],
  );

  const handleToggleVisibility = useCallback(
    (activity: fhir.CarePlanActivity) => {
      toggleVisibility(activity);
    },
    [toggleVisibility],
  );

  const hasNoVisibleItems = useMemo(() => {
    return displayCarePlan?.activity?.every(
      activity => activity.detail?.status !== 'unknown',
    );
  }, [displayCarePlan]);

  const [deletingActivity, setDeletingActivity] = useState<
    fhir.CarePlanActivity | undefined
  >(undefined);

  // Generate stable key for activity - since CarePlanActivity doesn't have ids, we use
  // system + title + notes as a compound key
  const getActivityKey = useCallback((activity: fhir.CarePlanActivity) => {
    const actionPlan = new ActionPlanActivity(activity);
    const title = actionPlan.getTitle();
    const notes = actionPlan.getDescription();
    const system = actionPlan.getSystem() || 'general';

    // there is a small risk of duplicates here, unlikely, but possible
    // in that case, react may fail to render the duplicate item
    return `${system}:${title}:${notes}`;
  }, []);

  const presentationGroups: Record<string, fhir.CarePlanActivity[]> = useMemo(
    () =>
      displayCarePlan?.activity
        ?.filter(activity =>
          viewMode === 'presentation-expanded' ||
          viewMode === 'presentation-compact'
            ? activity.detail?.status === 'unknown' // only show visible items in presentation mode
            : true,
        )
        .map(activity => {
          return new ActionPlanActivity(activity);
        })
        .reduce((acc, activity) => {
          let system = activity.getSystem() || 'general';
          if (!ACTION_PLAN_SYSTEMS.includes(system as any)) {
            // handle unknown systems
            system = 'general';
          }
          if (!acc[system]) {
            acc[system] = [];
          }
          acc[system].push(activity.activity);
          return acc;
        }, {} as Record<string, fhir.CarePlanActivity[]>) || {},
    [displayCarePlan, viewMode],
  );

  if (!executiveSummary) {
    return null;
  }
  return (
    <Box
      className={classes.actionsList}
      direction="column"
      fullHeight
      fullWidth
    >
      {hasNoVisibleItems &&
      (viewMode === 'presentation-compact' ||
        viewMode === 'presentation-expanded') ? (
        <Box
          className={classes.emptyStateContainer}
          direction="column"
          fullWidth
          fullHeight
        >
          <EmptyPageState
            message={formatMessage(messages.noActionItems)}
            illustration={CheckList}
          />
        </Box>
      ) : null}
      {ACTION_PLAN_SYSTEMS.map(system => {
        const Icon = ACTION_PLAN_SYSTEMS_ICONS[system];
        if (
          !presentationGroups[system] ||
          presentationGroups[system].length === 0
        ) {
          return null;
        }
        return (
          <Box key={system} direction="column">
            <Box className={classes.systemHeader}>
              <Icon width={16} height={16} />
              {formatMessage(systemHeaders[system])}
            </Box>
            {presentationGroups[system].map(activity => (
              <ActionActivity
                key={getActivityKey(activity)}
                activity={activity}
                viewMode={viewMode}
                onDelete={setDeletingActivity}
                onEdit={editActionPlanActivity}
                toggleVisibility={handleToggleVisibility}
              />
            ))}
          </Box>
        );
      })}
      <ActionItemModal
        title={
          selectedActionItem === undefined
            ? formatMessage(messages.addActionItem)
            : formatMessage(messages.editActionItem)
        }
        activity={activeActionItem}
        isOpen={isActionItemModalOpen}
        onDismiss={onActivityModalClose}
        onConfirm={onConfirm}
      />
      <DeleteModal
        isOpen={deletingActivity !== undefined}
        onDismiss={() => setDeletingActivity(undefined)}
        onConfirm={() => {
          if (deletingActivity) {
            setDeletingActivity(undefined);
            removeActivity(deletingActivity);
          }
        }}
        title={
          deletingActivity ? deletingActivity.detail?.description : undefined
        }
      />
    </Box>
  );
};
